import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';

import * as dashboardActions from '../../dashboard/actions';
import * as campaignActions from '../store/campaignActions';
import { withRouterV6 } from '../../../helpers/withRouterV6.jsx';
import type { AppDispatch, RootState } from '../../../store';
import EntityPerformancePage from '../../entity/performance/EntityPerformancePage';
import type {
  EntityDashboardConfiguration,
  EntityPerformanceDispatchProps,
  EntityPerformanceStateProps,
  EntityType,
  ListRequest,
  TimeBracketKey,
  SummaryRecordPath,
  SummaryDataRequest
} from '../../entity/performance/types';
import { buildTicketingSales, fetchCheckedInCount } from './ticketingSales';

type CampaignTicketingDispatchProps = {
  getTicketsRecord: (request: ListRequest) => void;
  getMerchandiseRecord: (request: ListRequest) => void;
};

type CampaignPerformanceStateProps = EntityPerformanceStateProps & {
  campaignId: string;
};

type CampaignPerformanceProps = CampaignPerformanceStateProps &
  EntityPerformanceDispatchProps &
  CampaignTicketingDispatchProps;

type CampaignPerformanceRouteParams = {
  campaignId?: string;
};

type CampaignPerformanceOwnProps = {
  match?: { params?: CampaignPerformanceRouteParams };
  params?: CampaignPerformanceRouteParams;
};

const CampaignPerformance: React.FC<CampaignPerformanceProps> = ({
  campaignId,
  getTicketsRecord,
  getMerchandiseRecord,
  ticketingSales,
  ...rest
}) => {
  const [checkedIn, setCheckedIn] = React.useState<number | null>(null);
  const ticketingEnabled = Boolean(ticketingSales?.enabled);

  React.useEffect(() => {
    // The page stays mounted across campaigns, so drop the previous campaign's count
    setCheckedIn(null);
    if (!campaignId || !ticketingEnabled) return;
    let cancelled = false;
    fetchCheckedInCount(campaignId).then((count) => {
      if (!cancelled) setCheckedIn(count);
    });
    return () => {
      cancelled = true;
    };
  }, [campaignId, ticketingEnabled]);

  React.useEffect(() => {
    if (!campaignId) return;
    const request: ListRequest & { includeDataTemplate: boolean } = {
      pageSize: 200,
      filters: [
        { key: 'campaignId', operator: 'EqualTo', value: campaignId },
        { key: 'enabled', operator: 'EqualTo', value: true }
      ],
      includeDataTemplate: true,
      resultType: 'Full'
    };
    getTicketsRecord(request);
    getMerchandiseRecord(request);
  }, [campaignId, getTicketsRecord, getMerchandiseRecord]);

  return (
    <EntityPerformancePage
      entityId={campaignId}
      entityType="campaign"
      ticketingSales={ticketingSales && { ...ticketingSales, checkedIn }}
      {...rest}
    />
  );
};

const mapState = (
  state: RootState,
  ownProps: CampaignPerformanceOwnProps
): CampaignPerformanceStateProps => {
  const campaignId = ownProps.match?.params?.campaignId ?? ownProps.params?.campaignId ?? '';

  return {
    campaignId,
    inited: Boolean(state.dashboard?.inited),
    modelRecord: state.campaign?.record as CampaignPerformanceStateProps['modelRecord'],
    summaryRecord: state.dashboard?.summaryRecord as CampaignPerformanceStateProps['summaryRecord'],
    previousSummaryRecord: state.dashboard?.previousSummaryRecord as CampaignPerformanceStateProps['previousSummaryRecord'],
    summaryDataRecord: state.dashboard?.summaryDataRecord as CampaignPerformanceStateProps['summaryDataRecord'],
    summaryType: (state.dashboard?.summaryType ?? null) as CampaignPerformanceStateProps['summaryType'],
    timePeriod: state.dashboard?.timePeriod as CampaignPerformanceStateProps['timePeriod'],
    timeBracket: state.dashboard?.timeBracket as CampaignPerformanceStateProps['timeBracket'],
    reportByDateAndMetricType: state.dashboard?.reportByDateAndMetricType as CampaignPerformanceStateProps['reportByDateAndMetricType'],
    configurations: (state.dashboard?.configurations ?? null) as CampaignPerformanceStateProps['configurations'],
    isSystemAdmin: get(state.session, 'isSystemAdmin') as CampaignPerformanceStateProps['isSystemAdmin'],
    topDonationsList: (get(state.dashboard, 'dashboardTopDonations.byPage[1]', []) ?? []) as CampaignPerformanceStateProps['topDonationsList'],
    topFundraisersList: (get(state.dashboard, 'dashboardTopFundraisers.byPage[1]', []) ?? []) as CampaignPerformanceStateProps['topFundraisersList'],
    ticketingSales: buildTicketingSales(state)
  };
};

const mapDispatch = (
  dispatch: AppDispatch
): EntityPerformanceDispatchProps & CampaignTicketingDispatchProps => ({
  init: (configurations: EntityDashboardConfiguration) => {
    dispatch(dashboardActions.init(configurations));
  },
  getSummaryRecord: (
    id: string,
    type: EntityType,
    timeBracket: TimeBracketKey,
    dateFrom?: string | null,
    dateTo?: string | null,
    path?: SummaryRecordPath
  ) => {
    const normalizedDateFrom = (dateFrom ?? null) as string | null;
    const normalizedDateTo = (dateTo ?? null) as string | null;
    dispatch(
      dashboardActions.getSummaryRecord(
        id,
        type,
        timeBracket,
        normalizedDateFrom,
        normalizedDateTo,
        path
      )
    );
  },
  getSummaryDataRecord: (request: SummaryDataRequest) => {
    dispatch(dashboardActions.getSummaryDataRecord(request));
  },
  onValueChange: (path: string, value: unknown) => {
    dispatch(dashboardActions.onValueChange(path, value));
  },
  getRecord: (id: string) => {
    dispatch(campaignActions.getRecord(id));
  },
  getTopDonationsListData: (listKey: string, id: string, request: ListRequest) => {
    dispatch(dashboardActions.getDonationListData(listKey, id, request));
  },
  getTopFundraisersListData: (listKey: string, id: string, request: ListRequest) => {
    dispatch(dashboardActions.getFundraiserListData(listKey, id, request));
  },
  getTicketsRecord: (request: ListRequest) => {
    dispatch(campaignActions.getTicketsRecord(request));
  },
  getMerchandiseRecord: (request: ListRequest) => {
    dispatch(campaignActions.getMerchandiseRecord(request));
  }
});

export default withRouterV6(connect(mapState, mapDispatch)(CampaignPerformance));